/**
 * Carbon progress indicator for multi-step flows (setup, the organization
 * wizard, a scan in progress): each step is a 2px line, a 16px mark and its
 * label. Marks are square (the Square Rule): a check for complete (neutral,
 * like every pass state), a ringed square with a filled core for the current
 * step, an empty ring for steps ahead, and the Coral octagon for an error.
 * Only the current step's line is Soft Green, as a tab line is. Horizontal
 * steps stack vertically below c-md. The current step carries
 * aria-current="step" and every step says its state to screen readers.
 * Vertical steps can carry `content` (the current step's action) inline
 * under their label, inside the step's line.
 */
import type { ReactNode } from 'react'
import clsx from 'clsx'
import { IconCheck, IconSeverityCrit } from './icons'

export type StepState = 'complete' | 'current' | 'incomplete' | 'error'

export interface ProgressStep {
  id: string
  label: ReactNode
  state: StepState
  /** A helper line under the label ("Optional", "3 of 8 workloads"). */
  helper?: ReactNode
  /**
   * Vertical only: content inline under this step, aligned with its label (the
   * current step's action: a form, a script to run). Ignored when horizontal.
   */
  content?: ReactNode
}

export interface ProgressIndicatorProps {
  steps: ProgressStep[]
  vertical?: boolean
  'aria-label'?: string
  className?: string
}

const STATE_WORD: Record<StepState, string> = {
  complete: 'complete',
  current: 'current',
  incomplete: 'not started',
  error: 'needs attention',
}

const LINE: Record<StepState, string> = {
  complete: 'border-line-strong',
  current: 'border-interactive-line',
  incomplete: 'border-line-subtle',
  error: 'border-line-subtle',
}

function Mark({ state }: { state: StepState }) {
  if (state === 'complete') return <IconCheck className="mt-0.5 text-text-primary" />
  if (state === 'error') return <IconSeverityCrit className="mt-0.5 text-sev-crit" />
  return (
    <span aria-hidden="true" className={clsx('mt-0.5 grid h-4 w-4 flex-none place-items-center ring-1 ring-inset', state === 'current' ? 'ring-text-primary' : 'ring-line-strong')}>
      {state === 'current' ? <span className="h-2 w-2 bg-text-primary" /> : null}
    </span>
  )
}

export function ProgressIndicator({ steps, vertical, 'aria-label': ariaLabel = 'Progress', className }: ProgressIndicatorProps) {
  return (
    <ol aria-label={ariaLabel} className={clsx('m-0 flex min-w-0 list-none flex-col p-0', !vertical && 'c-md:flex-row', className)}>
      {steps.map((step) => {
        const content = vertical && step.content ? step.content : null
        return (
          <li
            key={step.id}
            aria-current={step.state === 'current' ? 'step' : undefined}
            className={clsx(
              'flex min-w-0 items-start gap-2',
              content && 'flex-wrap',
              LINE[step.state],
              vertical
                ? 'border-l-2 pb-6 pl-3 last:pb-0'
                : 'border-l-2 pb-4 pl-3 last:pb-0 c-md:min-w-32 c-md:flex-1 c-md:border-l-0 c-md:border-t-2 c-md:pb-0 c-md:pl-0 c-md:pr-4 c-md:pt-2',
            )}
          >
            <Mark state={step.state} />
            <span className="min-w-0">
              <span className={clsx('block text-body', step.state === 'incomplete' ? 'text-text-secondary' : 'text-text-primary', step.state === 'current' && 'font-semibold')}>
                {step.label}
                <span className="sr-only">, {STATE_WORD[step.state]}</span>
              </span>
              {step.helper ? <span className="mt-0.5 block text-helper text-text-helper">{step.helper}</span> : null}
            </span>
            {/* Its own row under the label, indented past the 16px mark and the 8px gap. */}
            {content ? <div className="mt-4 w-full min-w-0 pl-6">{content}</div> : null}
          </li>
        )
      })}
    </ol>
  )
}

export default ProgressIndicator
